'use client';

import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { BaseLineDelete } from '@/app/components/icons/BaseLineDelete';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogFooter } from '@/app/components/ui/dialog';
import { useDeleteElements } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { ElementType } from '@/app/types';
import { useElementSelector } from '../../hooks/use-element-selector';
import type { ElementsSelectorProps } from '../../types';
import { ElementSelectorTabs } from '../ElementSelectorTabs';
import { ElementList as DialogElementList } from './ElementList';

/**
 * Dialog 模式的 Elements Selector
 */
export default function DialogSelector(props: ElementsSelectorProps) {
  const { isOpen, onClose, onDelete, onCreateOpen } = props;
  const t = useTranslations();

  // 使用统一的业务逻辑 Hook
  const selector = useElementSelector(props);

  const { userInfo } = useStore((s) => ({ userInfo: s.userInfo }));

  // 仅当为自己的主体或 type 为 preset 时可删除
  const deletableSelected = useMemo(
    () =>
      selector.selectedElements.filter(
        (el: ElementType) => el.type === 'preset' || (el.type === 'user' && el.creator_id === userInfo?.id),
      ),
    [selector.selectedElements, userInfo?.id],
  );

  const { handleDelete } = useDeleteElements({
    onSuccess: (deletedIds) => {
      onDelete?.(deletedIds);
      selector.setSelectedElements([]);
    },
  });

  const dialogProps = useMemo(
    () => ({
      open: isOpen,
      onOpenChange: (open: boolean) => {
        !open && onClose();
      },
    }),
    [isOpen, onClose],
  );

  if (!isOpen) return null;

  return (
    <Dialog {...dialogProps}>
      <DialogContent
        overlayStyle={{ zIndex: 40 }}
        style={{ zIndex: 41 }}
        className="flex h-[648px] min-w-[594px] max-w-[594px] flex-col gap-0 overflow-hidden rounded-12 border-none bg-[#17191B] px-4 py-4 text-white sm:max-w-fit sm:gap-0"
        closable={false}
        disableOverlayClose={true}
      >
        <DialogClose className="top-[22px] right-4 size-4" />
        <div className="flex-1 overflow-y-hidden">
          <ElementSelectorTabs {...selector} ElementListComponent={DialogElementList} onCreateOpen={onCreateOpen} />
        </div>

        <DialogFooter className="flex justify-between pt-4">
          <Button
            onClick={() => handleDelete(deletableSelected)}
            className="h-8 w-20 px-0 font-normal"
            disabled={!deletableSelected.length}
          >
            <BaseLineDelete className="mr-1" />
            {t('elements.delete')}
          </Button>
          <Button
            onClick={selector.handleDone}
            className="group flex h-8 w-36 items-center justify-center font-medium disabled:bg-none disabled:bg-system-neutral02 disabled:text-system-white24"
            size="default"
            variant="primary"
            disabled={!selector.selectedElements.length}
          >
            {t('elements.done')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
